import { createContext, useContext, useEffect, useState } from "react";
import api from "./api";

/*
 * Public runtime config (GET /api/config).
 *
 * Waitlist mode is a database-backed switch an admin can flip without a
 * redeploy, so the SPA cannot know it at build time — it has to ask.
 *
 * `ready` matters: until the answer arrives we do not know whether to show
 * "Start free" or "Join the waitlist", and rendering the wrong one and then
 * swapping it is worse than briefly rendering neither. Consumers gate on
 * `ready` rather than assuming a default.
 *
 * Failure is deliberately open (waitlistMode false). If /api/config is
 * unreachable the backend is down anyway, and a broken config fetch should
 * not silently lock everyone out of signing up.
 *
 * `googleClientId` is the public OAuth client id for Google sign-in. It is
 * runtime config (GOOGLE_CLIENT_ID on the backend), not a build arg, and ""
 * -- including after a failed fetch -- hides the Google button.
 */
const DEFAULT = { waitlistMode: false, disabledFeatures: [], googleClientId: "", ready: false };
const ConfigCtx = createContext({ ...DEFAULT, featureEnabled: () => true });

export function ConfigProvider({ children }) {
  const [state, setState] = useState(DEFAULT);

  useEffect(() => {
    let alive = true;
    api.get("/config")
      .then((r) => alive && setState({
        waitlistMode: !!r.data?.waitlist_mode,
        disabledFeatures: Array.isArray(r.data?.disabled_features) ? r.data.disabled_features : [],
        googleClientId: typeof r.data?.google_client_id === "string" ? r.data.google_client_id.trim() : "",
        ready: true,
      }))
      .catch(() => alive && setState({ ...DEFAULT, ready: true }));
    return () => { alive = false; };
  }, []);

  // UI hint only: the server refuses switched-off features regardless, so
  // failing open here can never expose one.
  const featureEnabled = (name) => !state.disabledFeatures.includes(name);

  return <ConfigCtx.Provider value={{ ...state, featureEnabled }}>{children}</ConfigCtx.Provider>;
}

export const useConfig = () => useContext(ConfigCtx);
